import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RaceService } from '../../services/race.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { Event, formatEventLocation } from '../../models/data.models';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { EventMapComponent, EventMapPoint } from '../../components/event-map/event-map.component';
import { RegionMappingService } from '../../services/region-mapping.service';
import { ResultService } from '../../services/result.service';
import { FFSLC_REGIONS, getFfslcRegionForDepartment } from '../../data/ffslc-regions';
import { RegionalThemeService } from '../../services/regional-theme.service';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { formatDistance } from '../../utils/format-distance';
import { currentSeasonStartYear, isPlausibleEventYear, seasonStartYear } from '../../utils/season';

type EventView = 'upcoming' | 'past';
type EventState = EventView | 'pending';

@Component({
  selector: 'app-races',
  standalone: true,
  imports: [CommonModule, TranslatePipe, RouterModule, EventMapComponent, BrandedLoaderComponent, PageHeaderComponent],
  templateUrl: './races.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RacesComponent {
  private readonly departmentNames: Record<string, string> = {
    '02': 'Aisne',
    '59': 'Nord',
    '60': 'Oise',
    '62': 'Pas-de-Calais',
    '80': 'Somme',
  };
  raceService = inject(RaceService);
  translationService = inject(TranslationService);
  authService = inject(AuthService);
  regionMappingService = inject(RegionMappingService);
  resultService = inject(ResultService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  readonly regionalTheme = inject(RegionalThemeService);
  private readonly mapSection = viewChild<ElementRef<HTMLElement>>('mapSection');
  private readonly resultsSection = viewChild<ElementRef<HTMLElement>>('resultsSection');
  private readonly firstEventSection = viewChild<ElementRef<HTMLElement>>('firstEventSection');

  viewMode = signal<'MAP_CENTERED' | 'SPLIT_VIEW'>('SPLIT_VIEW');
  eventView = signal<EventView>('past');
  readonly eventViews: { code: EventView; labelKey: string }[] = [
    { code: 'past', labelKey: 'races.eventViews.past' },
    { code: 'upcoming', labelKey: 'races.eventViews.upcoming' },
  ];

  regionFilter = signal<string | null>(null);
  federationRegionFilter = signal<string | null>(null);
  zoneFilter = signal<string | null>(null);
  yearFilter = signal<number | null>(null);
  private readonly currentSeason = currentSeasonStartYear();
  seasonFilter = signal<number | null>(this.currentSeason);
  departmentFilter = signal<string | null>(null);
  selectedEventId = signal<string | null>(null);
  userPosition = signal<{ latitude: number; longitude: number } | null>(null);
  locationError = signal<string | null>(null);
  readonly ffslcRegions = FFSLC_REGIONS;

  availableYears = computed(() => {
    const years = new Set<number>();
    this.races().forEach(r => {
      if (r.date) {
        const y = new Date(r.date).getFullYear();
        if (isPlausibleEventYear(y)) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  });

  availableSeasons = computed(() => {
    const current = this.currentSeason;
    const seasons = new Set<number>([current]);
    this.races().filter(race => race.isPublished === true).forEach(race => {
      const season = this.seasonStartYear(race.date);
      if (season !== null) seasons.add(season);
    });
    return [...seasons].sort((a, b) => {
      if (a === current) return -1;
      if (b === current) return 1;
      return b - a;
    });
  });

  constructor() {
    this.route.queryParams.subscribe(params => {
      this.regionFilter.set(params['region'] || null);
      this.federationRegionFilter.set(params['federation'] || null);
      this.zoneFilter.set(params['zone'] || null);
      this.yearFilter.set(params['year'] ? parseInt(params['year'], 10) : null);
      const season = Number(params['season']);
      this.seasonFilter.set(params['season'] === 'all' || params['year']
        ? null
        : Number.isInteger(season) && season >= 1990 && season <= 2100 ? season : this.currentSeason);
      this.departmentFilter.set(params['department'] || null);
      this.eventView.set(this.isEventView(params['view']) ? params['view'] : 'past');
      this.viewMode.set('SPLIT_VIEW');
    });
    effect(() => {
      const department = this.departmentFilter();
      const federation = this.federationRegionFilter();
      const administrativeRegion = this.regionFilter();
      if (department) this.regionalTheme.selectDepartment(department);
      else if (federation) this.regionalTheme.selectRegion(federation);
      else if (administrativeRegion) this.regionalTheme.selectAdministrativeRegion(administrativeRegion);
      else this.regionalTheme.selectRegion(null);
    });
    this.loadRaces();
  }

  async onRegionClicked(region: string) {
    if (!region) {
      this.clearFilter();
      return;
    }
    await this.router.navigate(['/races'], {
      queryParams: { region, federation: null, year: this.yearFilter(), department: null },
      queryParamsHandling: 'merge',
    });
    this.scrollToFirstEventOnMobile();
  }

  async onTerritoryRegionSelected(region: string): Promise<void> {
    await this.router.navigate(['/races'], {
      queryParams: { region, federation: null, department: null, year: this.yearFilter() },
      queryParamsHandling: 'merge',
    });
  }

  async onTerritoryFederationRegionSelected(region: string): Promise<void> {
    await this.router.navigate(['/races'], {
      queryParams: { federation: region, region: null, department: null, year: this.yearFilter() },
      queryParamsHandling: 'merge',
    });
  }

  async onTerritoryDepartmentSelected(department: string): Promise<void> {
    await this.router.navigate(['/races'], {
      queryParams: { department },
      queryParamsHandling: 'merge',
    });
    this.scrollToFirstEventOnMobile();
  }

  private scrollToFirstEventOnMobile(): void {
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 1023px)').matches) return;

    // Wait until Angular Router has completed its own scroll restoration and
    // the split view has acquired its final height.
    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        const section = this.firstEventSection()?.nativeElement ?? this.resultsSection()?.nativeElement;
        if (!section) return;
        const top = section.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth',
        });
      });
    }, 150);
  }

  private scrollToMapOnMobile(): void {
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 1023px)').matches) return;

    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        const section = this.mapSection()?.nativeElement;
        if (!section) return;
        const top = section.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth',
        });
      });
    }, 150);
  }

  onYearChange(year: string) {
    const yearNum = year ? parseInt(year, 10) : null;
    this.yearFilter.set(yearNum);
    this.router.navigate(['/races'], {
      queryParams: { year: yearNum },
      queryParamsHandling: 'merge',
    });
  }

  onSeasonChange(value: string): void {
    this.seasonFilter.set(value === 'all' ? null : Number(value));
    this.yearFilter.set(null);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { season: value, year: null, department: null },
      queryParamsHandling: 'merge',
    });
  }

  seasonStartYear(date: string | undefined): number | null {
    return seasonStartYear(date);
  }

  async onDepartmentChange(department: string): Promise<void> {
    this.departmentFilter.set(department || null);
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { department: department || null },
      queryParamsHandling: 'merge',
    });
    if (department) this.scrollToFirstEventOnMobile();
  }

  onFederationRegionChange(region: string): void {
    if (!region) {
      this.clearFilter();
      return;
    }
    this.router.navigate(['/races'], {
      queryParams: { federation: region, region: null, department: null },
      queryParamsHandling: 'merge',
    });
  }

  selectEventView(view: EventView) {
    this.eventView.set(view);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  clearFilter() {
    void this.showTerritoryOverview();
  }

  async showTerritoryOverview(): Promise<void> {
    this.regionalTheme.selectRegion(null);
    await this.router.navigate(['/races'], {
      queryParams: {
        region: null,
        federation: null,
        zone: null,
        department: null,
      },
      queryParamsHandling: 'merge',
    });
    this.scrollToMapOnMobile();
  }

  async goUpTerritoryLevel(): Promise<void> {
    if (this.departmentFilter()) {
      await this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { department: null },
        queryParamsHandling: 'merge',
      });
      this.scrollToMapOnMobile();
      return;
    }

    await this.showTerritoryOverview();
  }

  races = signal<Event[]>([]);
  raceResultCounts = signal<Record<string, number>>({});
  searchTerm = signal('');
  isLoading = signal(true);

  async loadRaces() {
    this.isLoading.set(true);
    try {
      const data = await this.raceService.getAllEvents(true);
      const raceIds = [...new Set(data.flatMap(event => event.races?.map(race => race.id) ?? []))];
      const fallbackCounts = Object.fromEntries(raceIds.map(raceId => [raceId, 0]));
      try {
        this.raceResultCounts.set(await this.resultService.getRaceParticipantCounts(raceIds));
      } catch (err) {
        // Never present unverified results as available when the count query fails.
        this.raceResultCounts.set(fallbackCounts);
        console.error('Error loading result availability for races:', err);
      }
      this.races.set(data);
    } catch (err) {
      console.error('Error loading races:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  onSearch(event: any) {
    this.searchTerm.set(event.target.value);
  }

  requestUserLocation(): void {
    this.locationError.set(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.locationError.set('La géolocalisation n’est pas disponible sur cet appareil.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => this.userPosition.set({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }),
      () => this.locationError.set('Position non autorisée. Vous pouvez continuer sans la partager.'),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  }

  // Strip accents and lowercase for comparison
  private normalize(s: string): string {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  // Map combined DB region names to individual SVG region names
  private regionAliases: Record<string, string[]> = {
    'paca / corse': ["provence-alpes-cote d'azur", 'corse'],
    'paca': ["provence-alpes-cote d'azur"],
    'bretagne / pays de la loire': ['bretagne', 'pays de la loire'],
  };

  private regionMatches(raceRegion: string, filterRegion: string): boolean {
    const normalizedRace = this.normalize(raceRegion);
    const normalizedFilter = this.normalize(filterRegion);

    // Direct match
    if (normalizedRace.includes(normalizedFilter) || normalizedFilter.includes(normalizedRace)) {
      return true;
    }

    // Check if DB region is a combined region that contains the filter
    const aliases = this.regionAliases[normalizedRace];
    if (aliases && aliases.some(a => a === normalizedFilter)) {
      return true;
    }

    // Check FFSLC mapping
    const mappedFfslcRegion = this.regionMappingService.getFfslcRegion(filterRegion);
    if (mappedFfslcRegion && normalizedRace.includes(this.normalize(mappedFfslcRegion))) {
      return true;
    }

    return false;
  }

  baseFilteredRaces = computed(() => {
    const regionF = this.regionFilter();
    const federationF = this.federationRegionFilter();
    const zoneF = this.zoneFilter();
    const yearF = this.yearFilter();
    const departmentF = this.departmentFilter();
    const seasonF = this.seasonFilter();
    const search = this.normalize(this.searchTerm());

    // Only show published events on the races page
    let allRaces = this.races().filter(race => race.isPublished === true);

    if (seasonF !== null) {
      allRaces = allRaces.filter(race => this.seasonStartYear(race.date) === seasonF);
    }

    if (yearF) {
      allRaces = allRaces.filter(race => {
        if (!race.date) return false;
        return new Date(race.date).getFullYear() === yearF;
      });
    }

    if (search) {
      allRaces = allRaces.filter(race => this.normalize([
        race.name,
        race.location,
        race.city,
        race.departmentCode,
        race.postalCode,
        race.region,
      ].filter(Boolean).join(' ')).includes(search));
    }

    if (regionF) {
      allRaces = allRaces.filter(race => {
        if (!race.region) return false;
        return this.regionMatches(race.region, regionF);
      });
    }

    if (federationF) {
      allRaces = allRaces.filter(race =>
        getFfslcRegionForDepartment(race.departmentCode)?.code === federationF,
      );
    }

    if (zoneF) {
      const regionsInZone = this.raceService.getRegionsInZone(zoneF.toUpperCase());
      allRaces = allRaces.filter(race =>
        race.region && regionsInZone.some(r => this.regionMatches(race.region!, r))
      );
    }

    if (departmentF) {
      allRaces = allRaces.filter(race => race.departmentCode === departmentF);
    }

    return allRaces;
  });

  eventViewCounts = computed<Record<EventView, number>>(() => {
    const counts: Record<EventView, number> = { upcoming: 0, past: 0 };
    this.baseFilteredRaces().forEach(race => {
      const state = this.getEventState(race);
      if (state !== 'pending') counts[state]++;
    });
    return counts;
  });

  filteredRaces = computed(() =>
    this.baseFilteredRaces().filter(race => this.getEventState(race) === this.eventView()),
  );

  availableDepartments = computed(() => {
    const departments = new Set<string>();
    const regionF = this.regionFilter();
    const federationF = this.federationRegionFilter();
    const yearF = this.yearFilter();
    const seasonF = this.seasonFilter();
    this.races().filter(race => race.isPublished === true).forEach(race => {
      if (!race.departmentCode) return;
      if (regionF && (!race.region || !this.regionMatches(race.region, regionF))) return;
      if (federationF && getFfslcRegionForDepartment(race.departmentCode)?.code !== federationF) return;
      if (yearF && (!race.date || new Date(race.date).getFullYear() !== yearF)) return;
      if (seasonF !== null && this.seasonStartYear(race.date) !== seasonF) return;
      departments.add(race.departmentCode);
    });
    return [...departments].sort((a, b) => a.localeCompare(b));
  });

  mapPoints = computed<EventMapPoint[]>(() => this.filteredRaces()
    .filter((race): race is Event & { latitude: number; longitude: number } =>
      typeof race.latitude === 'number' && typeof race.longitude === 'number')
    .map(race => ({
      id: race.id,
      name: race.name,
      city: race.city,
      departmentCode: race.departmentCode,
      latitude: race.latitude,
      longitude: race.longitude,
      precision: race.geoPrecision,
      state: this.getEventState(race),
    })));

  groupedRaces = computed(() => {
    const races = this.filteredRaces();
    const groups: { region: string; events: Event[] }[] = [];
    const map = new Map<string, Event[]>();
    
    races.forEach(r => {
      // Normalize region name for grouping
      let reg = r.region || 'Non attribuée';
      if (reg.includes('Hauts-de-France') || reg.includes('Région 3')) reg = 'Hauts-de-France';
      if (reg.includes('Île-de-France') || reg.includes('Région 1')) reg = 'Île-de-France';
      // ... we can add more if needed, but let's stick to a robust normalization
      
      if (!map.has(reg)) map.set(reg, []);
      map.get(reg)!.push(r);
    });

    Array.from(map.entries()).forEach(([region, events]) => {
      const userPosition = this.userPosition();
      if (userPosition) {
        events.sort((a, b) => this.distanceFromUser(a) - this.distanceFromUser(b));
      } else {
        // Upcoming events are chronological; published results show the latest first.
        const direction = this.eventView() === 'upcoming' ? 1 : -1;
        events.sort((a, b) => direction * (this.eventTimestamp(a) - this.eventTimestamp(b)));
      }
      groups.push({ region, events });
    });

    return groups.sort((a, b) => a.region.localeCompare(b.region));
  });

  totalPublishedCount = computed(() => this.baseFilteredRaces().length);
  selectedEventCount = computed(() => this.eventViewCounts()[this.eventView()]);
  selectedEventView = computed(() =>
    this.eventViews.find(view => view.code === this.eventView()) ?? this.eventViews[0],
  );
  totalCount = computed(() => this.races().length);

  getEventState(race: Event): EventState {
    const eventDate = this.eventTimestamp(race);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (Number.isFinite(eventDate) && eventDate >= today.getTime()) return 'upcoming';

    const hasResults = race.races?.some(
      eventRace => (this.raceResultCounts()[eventRace.id] ?? 0) > 0,
    ) ?? false;
    return hasResults ? 'past' : 'pending';
  }

  private eventTimestamp(race: Event): number {
    if (!race.date) return Number.NEGATIVE_INFINITY;
    const datePart = race.date.slice(0, 10);
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
    }
    const timestamp = new Date(race.date).getTime();
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
  }

  formatLocation(race: Event): string {
    return formatEventLocation(race) ||
      this.translationService.translate('races.locationPending');
  }

  formatDepartment(departmentCode: string): string {
    const name = this.departmentNames[departmentCode];
    return name ? `${name} (${departmentCode})` : this.translationService.translate('races.departmentFallback', departmentCode);
  }

  formatFfslcRegion(code: string): string {
    const region = this.ffslcRegions.find(item => item.code === code);
    return region ? `${region.code} · ${region.name}` : code;
  }

  distanceFromUser(race: Event): number {
    const user = this.userPosition();
    if (!user || race.latitude == null || race.longitude == null) return Number.POSITIVE_INFINITY;
    const toRadians = (degrees: number) => degrees * Math.PI / 180;
    const deltaLat = toRadians(race.latitude - user.latitude);
    const deltaLon = toRadians(race.longitude - user.longitude);
    const a = Math.sin(deltaLat / 2) ** 2 +
      Math.cos(toRadians(user.latitude)) * Math.cos(toRadians(race.latitude)) *
      Math.sin(deltaLon / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  distanceLabel(race: Event): string | null {
    const distance = this.distanceFromUser(race);
    if (!Number.isFinite(distance)) return null;
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return `${formatDistance(distance, locale)} km`;
  }

  selectEventOnMap(eventId: string): void {
    this.selectedEventId.set(eventId);
  }

  onMapEventSelected(eventId: string): void {
    this.selectedEventId.set(eventId);
    if (typeof document === 'undefined') return;
    window.requestAnimationFrame(() => {
      document.getElementById(`event-card-${eventId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }

  private isEventView(value: unknown): value is EventView {
    return value === 'upcoming' || value === 'past';
  }

  formatDate(isoString: string): string {
    if (!isoString) return this.translationService.translate('dataStats.unknownDate');
    const date = new Date(isoString);
    if (isNaN(date.getTime()) || date.getFullYear() < 1990) {
      return this.translationService.translate('dataStats.unknownDate');
    }
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  async deleteEvent(event: any, race: Event) {
    event.stopPropagation(); // Avoid navigating to details
    if (confirm(this.translationService.translate('common.deleteEventConfirm', race.name))) {
      try {
        await this.raceService.deleteEvent(race.id);
        await this.loadRaces(); // Reload list
      } catch (err) {
        alert(this.translationService.translate('common.deleteError'));
      }
    }
  }
}
